/**
 * Shared rules for primary CTAs on multi-page donation flows (Standard, Crowdfunding, etc.).
 *
 * Use the same conditions as the step's onClick handler: if the user advances to another
 * in-form page before checkout (custom inputs, monthly bump), the label is "Next".
 * Otherwise leave undefined so FormActionButtons / CustomInputs render Support / Add to Cart.
 *
 * Permutation matrix (START_PAGE primary CTA via getPreCheckoutStepPrimaryButtonLabel):
 *
 * | custom inputs step | monthly bump after start* | START button |
 * |--------------------|---------------------------|--------------|
 * | no                 | no                        | Support / ATC |
 * | no                 | yes                       | Next         |
 * | yes                | no                        | Next         |
 * | yes                | yes                       | Next         |
 *
 * *Monthly bump "after start" means handleButtonClick would open MONTHLY_BUMP_PAGE when
 * there are zero custom inputs — same predicate as shouldShowMonthlyBump() in the forms.
 *
 * CUSTOM_INPUTS last field page: pass primaryButtonLabel from getPrimaryLabelAfterCustomInputsStep
 * so the button reads "Next" when handleCustomInputsPageSubmit routes to MONTHLY_BUMP_PAGE.
 */

/**
 * @param {object} opts
 * @param {boolean} [opts.hasCustomInputsStep] — START_PAGE navigates to CUSTOM_INPUTS_PAGE
 * @param {boolean} [opts.hasMonthlyBumpStep] — START_PAGE or CUSTOM_INPUTS navigates to MONTHLY_BUMP_PAGE
 * @returns {'Next'|undefined}
 */
export function getPreCheckoutStepPrimaryButtonLabel({
  hasCustomInputsStep = false,
  hasMonthlyBumpStep = false,
} = {}) {
  if (hasCustomInputsStep || hasMonthlyBumpStep) {
    return 'Next';
  }
  return undefined;
}

/**
 * Last custom-inputs step: show "Next" when the next screen is MONTHLY_BUMP_PAGE (not checkout).
 * Mirrors handleCustomInputsPageSubmit's shouldShowMonthlyBump branch.
 *
 * @param {boolean} monthlyBumpFollowsCustomInputs — same as shouldShowMonthlyBump() at submit time
 * @returns {'Next'|undefined}
 */
export function getPrimaryLabelAfterCustomInputsStep(monthlyBumpFollowsCustomInputs) {
  return monthlyBumpFollowsCustomInputs ? 'Next' : undefined;
}

// The frequency word a "Give $X …" label ends with (Paper 2JU-0 / H6W-0:
// "Give $25", "Give $25 monthly"). One-time gifts carry no word.
const CTA_FREQUENCY_WORD = {
  DAILY: 'daily',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  ANNUALLY: 'yearly',
};

/** Whole dollars on buttons, cents only when the amount has them (Paper 00 Decisions, Oct 8). */
export function formatCtaAmount(value) {
  const numeric = typeof value === 'number' ? value : Number(String(value ?? '').replace(/[$,]/g, ''));
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  const hasCents = Math.round(numeric * 100) % 100 !== 0;
  return `$${numeric.toLocaleString('en-US', {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * The form's primary label when it opens checkout (Paper 00 Decisions 4NE-0: "CTAs state the
 * amount; one naming rule across surfaces"; 2JU-0, H6W-0, 4K0-0):
 * - donations: "Give $50" / "Give $25 monthly" ("Give" until an amount is picked)
 * - add to cart on: "Add to cart"
 * - tickets: "Get 2 tickets · $250" ("Get tickets" until a ticket is picked; no price when free)
 *
 * @param {object} opts
 * @param {'STANDARD'|'PRODUCT'|'CROWDFUNDING'|'EVENT'} [opts.formType]
 * @param {boolean} [opts.addToCart]
 * @param {number|string} [opts.amount] - the amount the button will charge (ticket total for events)
 * @param {string} [opts.frequency] - ONE_TIME | DAILY | WEEKLY | MONTHLY | ANNUALLY
 * @param {number} [opts.ticketCount]
 * @param {boolean} [opts.converted] - the form shows a converted display currency. The button then
 *   states no amount ("Give", "Get tickets"): a form's amount can be USD or display-currency
 *   depending on the form type, and a "$" figure on a converted form would be wrong (owner call,
 *   2026-10-20 release merge).
 * @returns {string}
 */
export function getFormCheckoutButtonLabel({
  formType = 'STANDARD',
  addToCart = false,
  amount,
  frequency = 'ONE_TIME',
  ticketCount = 0,
  converted = false,
} = {}) {
  if (formType === 'EVENT') {
    if (converted) return 'Get tickets';
    const count = Number(ticketCount) || 0;
    if (count <= 0) return 'Get tickets';
    const tickets = `Get ${count} ${count === 1 ? 'ticket' : 'tickets'}`;
    const price = formatCtaAmount(amount);
    return price ? `${tickets} · ${price}` : tickets;
  }
  if (addToCart) return 'Add to cart';
  if (converted) return 'Give';
  const price = formatCtaAmount(amount);
  if (!price) return 'Give';
  const word = CTA_FREQUENCY_WORD[frequency];
  return word ? `Give ${price} ${word}` : `Give ${price}`;
}

/** The frequency the CTA states: a form with one frequency always charges that one. */
export function getCtaFrequency(frequencies = [], frequency = '') {
  if (Array.isArray(frequencies) && frequencies.length === 1) return frequencies[0];
  return frequency || 'ONE_TIME';
}
